
import { useEffect, useState } from 'react';
import { X, Pencil } from 'lucide-react';
import { useUpdateProperty } from 'features/property/hooks/useUpdateProperty';
import { useUploadPropertyPhoto } from 'features/property/hooks/useUploadPropertyPhoto';
import { getS3KeyFromUrl } from 'utils/s3Helper';
import { ImagePlus, Star, Trash2 } from 'lucide-react';

export const EditPropertyModal = ({ isOpen, onClose, property }) => {
  const { mutateAsync: updateProperty, isPending } = useUpdateProperty();

  const { mutateAsync: uploadPhoto, isPending: isUploading } = useUploadPropertyPhoto();
const [photos, setPhotos] = useState([]);

  const [formData, setFormData] = useState({
    propertyLocation: '',
    propertyPrice: '',
    propertyArea: '',
    bedrooms: '',
    balcony: '',
    furnishing: '',
    totalPropertyFloor: '',
    flatNumber: '',
    constructionStatus: '',
    approvalStatus: '',
    status: '',
    ownerName: '',
    ownerType: '',
    currentAddress: '',
    currentPincode: '',
  });

useEffect(() => {
  if (property) {
    setFormData({
      // ...your existing fields...
    });

    setPhotos(
      (property.photos || []).map((p) => ({
        _id: p._id,
        key: getS3KeyFromUrl(p.url),
        preSignedUrl: p.url, // already a viewable URL for existing photos
        caption: p.caption || '',
        isPrimary: p.isPrimary || false,
        uploadedAt: p.uploadedAt,
      }))
    );
  }
}, [property]);

  if (!isOpen || !property) return null;

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

 const handleSubmit = async () => {
  try {
    const updateData = { ...formData };

    // numeric field conversion (from earlier fixes)
    const numericFields = ['propertyPrice', 'propertyArea', 'bedrooms', 'balcony', 'totalPropertyFloor'];
    numericFields.forEach((field) => {
      updateData[field] = updateData[field] === '' ? null : Number(updateData[field]);
    });

    // build photos payload — store S3 keys, not pre-signed URLs
    updateData.photos = photos.map((p) => ({
      url: p.key,
      caption: p.caption,
      isPrimary: p.isPrimary,
      uploadedAt: p.uploadedAt,
    }));

    await updateProperty({
      propertyId: property._id,
      data: updateData,
    });

    onClose();
  } catch (error) {
    console.error('Error updating property:', error);
  }
};

const isBusy = isPending || isUploading;






const handlePhotoUpload = async (e) => {
  const files = Array.from(e.target.files || []);
  if (files.length === 0) return;

  for (const file of files) {
    try {
      const { key, preSignedUrl } = await uploadPhoto({ file });

      setPhotos((prev) => [
        ...prev,
        {
          key,
          preSignedUrl,
          caption: '',
          isPrimary: prev.length === 0, // first photo becomes primary by default
          uploadedAt: new Date().toISOString(),
        },
      ]);
    } catch (err) {
      // toast already handled inside useUploadPropertyPhoto's onError
      console.error('Photo upload failed:', err);
    }
  }

  e.target.value = ''; // allow re-selecting the same file later
};

const handleRemovePhoto = (index) => {
  setPhotos((prev) => {
    const next = prev.filter((_, i) => i !== index);
    // if the removed photo was primary, promote the first remaining one
    if (prev[index]?.isPrimary && next.length > 0 && !next.some((p) => p.isPrimary)) {
      next[0] = { ...next[0], isPrimary: true };
    }
    return next;
  });
};

const handleSetPrimary = (index) => {
  setPhotos((prev) => prev.map((p, i) => ({ ...p, isPrimary: i === index })));
};

const handleCaptionChange = (index, value) => {
  setPhotos((prev) =>
    prev.map((p, i) => (i === index ? { ...p, caption: value } : p))
  );
};

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
      <div className='bg-card rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto'>

        {/* Header */}
        <div className='sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between z-10'>
          <h2 className='text-2xl font-bold text-foreground flex items-center gap-2'>
            <Pencil className='text-blue-500' size={24} />
            Edit Property
          </h2>
          <button onClick={onClose} disabled={isBusy} className='p-2 hover:bg-muted rounded-lg transition-colors'>
            <X size={24} />
          </button>
        </div>

        <div className='p-6 space-y-6'>



          {/* Photos */}
<div className='bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6'>
  <h3 className='text-lg font-semibold text-blue-900 dark:text-blue-100 mb-4'>
    Photos
  </h3>

  <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-4'>
    {photos.map((photo, index) => (
      <div key={photo._id || photo.key} className='relative border border-border rounded-lg overflow-hidden bg-background'>
        <img
          src={photo.preSignedUrl}
          alt={photo.caption || `Property photo ${index + 1}`}
          className='w-full h-32 object-cover'
        />

        {/* Primary toggle */}
        <button
          type='button'
          onClick={() => handleSetPrimary(index)}
          title={photo.isPrimary ? 'Primary photo' : 'Set as primary'}
          className={`absolute top-2 left-2 p-1 rounded-full ${
            photo.isPrimary ? 'bg-yellow-400 text-white' : 'bg-black/40 text-white hover:bg-black/60'
          }`}
        >
          <Star size={14} fill={photo.isPrimary ? 'currentColor' : 'none'} />
        </button>

        {/* Remove */}
        <button
          type='button'
          onClick={() => handleRemovePhoto(index)}
          title='Remove photo'
          className='absolute top-2 right-2 p-1 rounded-full bg-black/40 text-white hover:bg-red-600'
        >
          <Trash2 size={14} />
        </button>

        {/* Caption */}
        <input
          type='text'
          value={photo.caption}
          onChange={(e) => handleCaptionChange(index, e.target.value)}
          placeholder='Caption (optional)'
          className='w-full px-2 py-1 text-xs border-t border-border bg-background text-foreground'
        />
      </div>
    ))}

    {/* Upload tile */}
    <label className='flex flex-col items-center justify-center h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-accent transition-colors text-muted-foreground'>
      <ImagePlus size={24} />
      <span className='text-xs mt-1'>{isUploading ? 'Uploading...' : 'Add Photo'}</span>
      <input
        type='file'
        accept='image/*'
        multiple
        onChange={handlePhotoUpload}
        disabled={isUploading}
        className='hidden'
      />
    </label>
  </div>

  {photos.length === 0 && (
    <p className='text-sm text-muted-foreground'>No photos uploaded yet.</p>
  )}
</div>

          {/* Basic Information */}
          <div className='bg-purple-50 dark:bg-purple-900/20 rounded-lg p-6'>
            <h3 className='text-lg font-semibold text-purple-900 dark:text-purple-100 mb-4'>
              Basic Information
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div className='md:col-span-2'>
                <label className='block text-sm text-muted-foreground mb-1'>Property Location</label>
                <input
                  type='text'
                  name='propertyLocation'
                  value={formData.propertyLocation}
                  onChange={handleChange}
                  className='w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground'
                />
              </div>
              <div>
                <label className='block text-sm text-muted-foreground mb-1'>Property Category</label>
                <input
                  type='text'
                  value={property.propertyCategory || ''}
                  disabled
                  className='w-full px-3 py-2 border border-border rounded-lg bg-muted text-muted-foreground capitalize cursor-not-allowed'
                />
              </div>
              <div>
                <label className='block text-sm text-muted-foreground mb-1'>Property Type</label>
                <input
                  type='text'
                  value={property.propertyType?.replace(/_/g, ' ') || ''}
                  disabled
                  className='w-full px-3 py-2 border border-border rounded-lg bg-muted text-muted-foreground capitalize cursor-not-allowed'
                />
              </div>
              <div>
                <label className='block text-sm text-muted-foreground mb-1'>Property Price (₹)</label>
                <input
                  type='number'
                  name='propertyPrice'
                  value={formData.propertyPrice}
                  onChange={handleChange}
                  className='w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground'
                />
              </div>
            </div>
          </div>

          {/* Property Details */}
          <div className='bg-green-50 dark:bg-green-900/20 rounded-lg p-6'>
            <h3 className='text-lg font-semibold text-green-900 dark:text-green-100 mb-4'>
              Property Details
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              <div>
                <label className='block text-sm text-muted-foreground mb-1'>Property Area (sq ft)</label>
                <input
                  type='number'
                  name='propertyArea'
                  value={formData.propertyArea}
                  onChange={handleChange}
                  className='w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground'
                />
              </div>
              <div>
                <label className='block text-sm text-muted-foreground mb-1'>Bedrooms</label>
                <input
                  type='number'
                  name='bedrooms'
                  value={formData.bedrooms}
                  onChange={handleChange}
                  className='w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground'
                />
              </div>
              <div>
                <label className='block text-sm text-muted-foreground mb-1'>Balcony</label>
                <input
                  type='number'
                  name='balcony'
                  value={formData.balcony}
                  onChange={handleChange}
                  className='w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground'
                />
              </div>
              <div>
                <label className='block text-sm text-muted-foreground mb-1'>Furnishing</label>
                <select
                  name='furnishing'
                  value={formData.furnishing}
                  onChange={handleChange}
                  className='w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground'
                >
                  <option value=''>Select</option>
                  <option value='unfurnished'>Unfurnished</option>
                  <option value='semi_furnished'>Semi Furnished</option>
                  <option value='fully_furnished'>Fully Furnished</option>
                </select>
              </div>
              <div>
                <label className='block text-sm text-muted-foreground mb-1'>Total Property Floor</label>
                <input
                  type='number'
                  name='totalPropertyFloor'
                  value={formData.totalPropertyFloor}
                  onChange={handleChange}
                  className='w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground'
                />
              </div>
              <div>
                <label className='block text-sm text-muted-foreground mb-1'>Flat Number</label>
                <input
                  type='text'
                  name='flatNumber'
                  value={formData.flatNumber}
                  onChange={handleChange}
                  className='w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground'
                />
              </div>
              <div>
                <label className='block text-sm text-muted-foreground mb-1'>Construction Status</label>
                <select
                  name='constructionStatus'
                  value={formData.constructionStatus}
                  onChange={handleChange}
                  className='w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground'
                >
                  <option value=''>Select</option>
                  <option value='ready_to_move'>Ready to Move</option>
                  <option value='under_construction'>Under Construction</option>
                  <option value='new_launch'>New Launch</option>
                </select>
              </div>
            </div>
          </div>

          {/* Address — read only */}
          <div className='bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-6'>
            <h3 className='text-lg font-semibold text-yellow-900 dark:text-yellow-100 mb-4'>
              Address
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div className='md:col-span-2'>
                <p className='text-sm text-muted-foreground'>Address Line</p>
                <p className='font-medium text-foreground'>{property.address?.addressLine}</p>
              </div>
              <div>
                <p className='text-sm text-muted-foreground'>City</p>
                <p className='font-medium text-foreground'>{property.address?.city}</p>
              </div>
              <div>
                <p className='text-sm text-muted-foreground'>State</p>
                <p className='font-medium text-foreground'>{property.address?.state}</p>
              </div>
              <div>
                <p className='text-sm text-muted-foreground'>Country</p>
                <p className='font-medium text-foreground'>{property.address?.country}</p>
              </div>
              <div>
                <p className='text-sm text-muted-foreground'>Pincode</p>
                <p className='font-medium text-foreground'>{property.address?.pincode}</p>
              </div>
            </div>
          </div>

          {/* Owner Information */}
          {/* <div className='bg-pink-50 dark:bg-pink-900/20 rounded-lg p-6'>
            <h3 className='text-lg font-semibold text-pink-900 dark:text-pink-100 mb-4'>
              Owner Information
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm text-muted-foreground mb-1'>Owner Type</label>
                <select
                  name='ownerType'
                  value={formData.ownerType}
                  onChange={handleChange}
                  className='w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground'
                >
                  <option value=''>Select</option>
                  <option value='owner'>Owner</option>
                  <option value='agent'>Agent</option>
                  <option value='builder'>Builder</option>
                </select>
              </div>
              <div>
                <label className='block text-sm text-muted-foreground mb-1'>Owner Name</label>
                <input
                  type='text'
                  name='ownerName'
                  value={formData.ownerName}
                  onChange={handleChange}
                  className='w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground'
                />
              </div>
              <div>
                <label className='block text-sm text-muted-foreground mb-1'>Current Address</label>
                <input
                  type='text'
                  name='currentAddress'
                  value={formData.currentAddress}
                  onChange={handleChange}
                  className='w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground'
                />
              </div>
              <div>
                <label className='block text-sm text-muted-foreground mb-1'>Current Pincode</label>
                <input
                  type='text'
                  name='currentPincode'
                  value={formData.currentPincode}
                  onChange={handleChange}
                  className='w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground'
                />
              </div>
            </div>
          </div> */}

          {/* Seller Information — read only */}
          {property.sellerId && (
            <div className='bg-orange-50 dark:bg-orange-900/20 rounded-lg p-6'>
              <h3 className='text-lg font-semibold text-orange-900 dark:text-orange-100 mb-4'>
                Seller Information
              </h3>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <p className='text-sm text-muted-foreground'>Name</p>
                  <p className='font-medium text-foreground'>
                    {property.sellerId.firstName} {property.sellerId.lastName}
                  </p>
                </div>
                <div>
                  <p className='text-sm text-muted-foreground'>Email</p>
                  <p className='font-medium text-foreground'>{property.sellerId.email}</p>
                </div>
                <div>
                  <p className='text-sm text-muted-foreground'>Phone</p>
                  <p className='font-medium text-foreground'>{property.sellerId.phoneNumber}</p>
                </div>
              </div>
            </div>
          )}

          {/* Status */}
         <div className='bg-gray-50 dark:bg-gray-900/20 rounded-lg p-6'>
  <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4'>
    Status
  </h3>
  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
    <div>
      <label className='block text-sm text-muted-foreground mb-1'>Approval Status</label>
      <select
        name='approvalStatus'
        value={formData.approvalStatus}
        onChange={handleChange}
        className='w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground'
      >
        {/* <option value='pending' disabled={formData.approvalStatus === 'pending'}>Pending</option> */}
        <option value='approved' disabled={formData.approvalStatus === 'approved'}>Approved</option>
        <option value='rejected' disabled={formData.approvalStatus === 'rejected'}>Rejected</option>
      </select>
    </div>
    <div>
      <label className='block text-sm text-muted-foreground mb-1'>Property Status</label>
      <select
        name='status'
        value={formData.status}
        onChange={handleChange}
        className='w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground'
      >
        <option value='active' disabled={formData.status === 'active'}>Active</option>
        <option value='inactive' disabled={formData.status === 'inactive'}>Inactive</option>
      </select>
    </div>
  </div>
</div>

          {/* Footer */}
          <div className='flex justify-end gap-3 pt-2'>
            <button
              onClick={onClose}
              disabled={isBusy}
              className='px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors disabled:opacity-50'
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isBusy}
              className='px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50'
            >
              {isPending ? 'Updating...' : 'Update Property'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};